import { FC } from 'react'
import { Control, UseFormSetValue, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline'
import { Box, Chip, IconButton, Typography } from '@mui/material'

import { CustomerAutocomplete } from '../../../components/forms/fields/CustomerAutocomplete'
import { SkierFieldArray } from '../../../components/forms/SkierFieldArray'
import { LessonFormValues } from './schema'

type CustomerPaymentStatus = 'paid' | 'partially_paid' | null

type Props = {
  index: number
  control: Control<LessonFormValues>
  setValue: UseFormSetValue<LessonFormValues>
  onRemove: () => void
  canRemove: boolean
  prices?: number[]
  paymentStatus?: CustomerPaymentStatus
  onViewCustomer?: (customerId: string) => void
  disabled?: boolean
}

export const CustomerOrderSection: FC<Props> = ({
  index,
  control,
  setValue,
  onRemove,
  canRemove,
  prices,
  paymentStatus,
  onViewCustomer,
  disabled,
}) => {
  const { t } = useTranslation()
  const customerId = useWatch({ control, name: `bookings.${index}.customerId` })

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        p: 1.5,
        mb: 1,
      }}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="subtitle2">
            {t('lessons.form.customer')} #{index + 1}
          </Typography>
          {paymentStatus && (
            <Chip
              size="small"
              label={t(`paymentStatus.${paymentStatus}`)}
              color={paymentStatus === 'paid' ? 'success' : 'warning'}
            />
          )}
        </Box>
        {canRemove && !disabled && (
          <IconButton size="small" onClick={onRemove} color="error">
            <RemoveCircleOutlineIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      <Box display="flex" alignItems="flex-start" gap={0.5}>
        <Box flex={1}>
          <CustomerAutocomplete
            control={control}
            setValue={setValue}
            customerIdField={`bookings.${index}.customerId`}
            clientNameField={`bookings.${index}.clientName`}
            disabled={disabled}
          />
        </Box>
        {onViewCustomer && customerId && (
          <IconButton size="small" onClick={() => onViewCustomer(customerId)} sx={{ mt: 0.25 }}>
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      <Box sx={{ mt: 1 }}>
        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
          {t('lessons.form.skiers')}
        </Typography>
        <SkierFieldArray control={control} name={`bookings.${index}.skiers`} prices={prices} disabled={disabled} />
      </Box>
    </Box>
  )
}
